import React, { useEffect, useState, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";


// --- Supabase Config ---
const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  "";
const SUPABASE_ANON_KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  "";
const ADMIN_PASSWORD =
  process.env.REACT_APP_ADMIN_PASSWORD ||
  process.env.NEXT_PUBLIC_ADMIN_PASSWORD ||
  "chessadmin";

const supabase =
  SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
    : null;

// --- Helpers ---
function getPlayerKey(name) {
  if (!name) return "";
  return name.trim().toLowerCase().replace(/\s+/g, "_");
}

function splitName(fullName) {
  if (!fullName) return { firstName: "", lastName: "_" };
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0];
  const lastName = parts.slice(1).join(" ") || "_";
  return { firstName, lastName };
}

// ELO calculation with K=32
function calculateEloChange(playerRating, opponentRating, result) {
  const K = 32;
  const expectedScore = 1 / (1 + Math.pow(10, (opponentRating - playerRating) / 400));
  const actualScore = result === "win" ? 1 : result === "draw" ? 0.5 : 0;
  return Math.round(K * (actualScore - expectedScore));
}

// --- Main Component ---
export default function ChessClubVisualizerAHS() {
  const [players, setPlayers] = useState([]);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [expandedRounds, setExpandedRounds] = useState({});
  const [isAdminUnlocked, setIsAdminUnlocked] = useState(false);

  // Admin fields
  const [pasteText, setPasteText] = useState("");
  const [sideEventText, setSideEventText] = useState("");
  const [sideEventTeam, setSideEventTeam] = useState("");
  const [sideEventStatus, setSideEventStatus] = useState("");

  // Manual match addition
  const [manualPlayer, setManualPlayer] = useState("");
  const [manualOpponent, setManualOpponent] = useState("");
  const [manualResult, setManualResult] = useState("win");
  const [manualColor, setManualColor] = useState("white");
  const [manualDate, setManualDate] = useState(new Date().toISOString().split('T')[0]);
  const [manualStatus, setManualStatus] = useState("");

  // Rating edit
  const [ratingEditPlayer, setRatingEditPlayer] = useState("");
  const [ratingEditValue, setRatingEditValue] = useState("");
  const [ratingEditStatus, setRatingEditStatus] = useState("");

  // Load from Supabase
  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    if (!supabase) {
      setStatus("Supabase not configured");
      return;
    }

    try {
      setStatus("Loading from Supabase...");
      
      const { data: playersData, error: playersError } = await supabase
        .from("player_ahs")
        .select("*")
        .order("created_at", { ascending: true });

      if (playersError) throw playersError;

      const { data: eventsData, error: eventsError } = await supabase
        .from("events_ahs")
        .select("*")
        .order("created_at", { ascending: true });

      if (eventsError) throw eventsError;

      setPlayers(playersData || []);
      setEvents(eventsData || []);
      setStatus("");
    } catch (e) {
      setStatus("Failed to load: " + e.message);
    }
  }

  // Derive all games from events
// Derive all games from events
  const allGames = useMemo(() => {
    const games = [];
    for (const event of events) {
      if (event.event_format !== 'tournament') continue;
      
      const eventData = event.event_data || {};
      const rounds = eventData.roundList || [];
      
      rounds.forEach((round, rIdx) => {
        (round || []).forEach((g) => {
          games.push({
            ...g,
            eventName: event.name,
            eventId: event.id,
            round: rIdx + 1,
            date: eventData.date || event.created_at,
            // Ensure rating data is preserved
            whiteOrigRating: g.whiteOrigRating,
            whiteNewRating: g.whiteNewRating,
            blackOrigRating: g.blackOrigRating,
            blackNewRating: g.blackNewRating,
          });
        });
      });
    }
    return games;
  }, [events]);

  // Player list with game counts
  const playerList = useMemo(() => {
    return players
      .map((p) => {
        const playerName = p.name;
        const officialGames = allGames.filter(
          (g) => g.whiteId === playerName || g.blackId === playerName
        ).length;
        
        const sideEventGames = (p.match_history || []).filter(
          (m) => m.event_format === 'side'
        ).length;
        
        const currentRating = p.rating_history && p.rating_history.length > 0
          ? p.rating_history[p.rating_history.length - 1]
          : 1200;
        
        return {
          ...p,
          currentRating,
          officialGames,
          sideEventGames,
          totalGames: officialGames + sideEventGames,
        };
      })
      .filter((p) => p.totalGames > 0)
      .sort((a, b) => {
        if (b.currentRating !== a.currentRating) {
          return b.currentRating - a.currentRating;
        }
        return b.totalGames - a.totalGames;
      });
  }, [players, allGames]);

  // Tournament list
  const tournamentList = useMemo(() => {
    return events
      .filter((e) => e.event_format === 'tournament')
      .map((e) => ({
        ...e,
        eventData: e.event_data || {},
        playerCount: (e.event_data?.playerIds || []).length,
        roundCount: (e.event_data?.roundList || []).length,
        gameCount: (e.event_data?.roundList || []).reduce(
          (acc, r) => acc + (r?.length || 0),
          0
        ),
      }));
  }, [events]);

  // Selected player data
  const selectedPlayerData = useMemo(() => {
    if (!selectedPlayer) return null;
    
    const player = players.find((p) => p.id === selectedPlayer);
    if (!player) return null;

    const playerName = player.name;
    const officialGames = allGames.filter(
      (g) => g.whiteId === playerName || g.blackId === playerName
    );

    const ratingHistory = player.rating_history || [];
    const historyData = ratingHistory.map((rating, idx) => ({
      index: idx + 1,
      rating,
    }));

    const sideEventMatches = (player.match_history || []).filter(
      (m) => m.event_format === 'side'
    );

    return {
      player,
      playerName,
      officialGames,
      sideEventMatches,
      ratingHistory: historyData,
    };
  }, [selectedPlayer, players, allGames]);

  // --- Admin Functions ---
  function handleUnlockAdmin() {
    const pwd = prompt("Enter admin password:");
    if (pwd === ADMIN_PASSWORD) {
      setIsAdminUnlocked(true);
      alert("Admin controls unlocked.");
    } else {
      alert("Incorrect password.");
    }
  }

  async function handlePasteMerge() {
    try {
      const parsed = JSON.parse(pasteText);
      
      // Handle old format - convert to new schema
      if (parsed.players || parsed.tournaments) {
        await convertOldFormat(parsed);
      } else if (parsed.event_data) {
        // New format event
        await saveEvent(parsed);
      }
      
      await loadData();
      setPasteText("");
      setStatus("Data saved successfully");
    } catch (e) {
      setStatus("Error: " + e.message);
    }
  }

  async function convertOldFormat(oldData) {
    if (!supabase) return;

    // Build ID to name mapping from players
    const idToName = {};
    const playersMap = {};
    
    for (const [id, p] of Object.entries(oldData.players || {})) {
      const name = `${p.firstName} ${p.lastName}`.trim();
      idToName[id] = name;
      const playerKey = getPlayerKey(name);
      
      if (!playersMap[playerKey]) {
        playersMap[playerKey] = {
          name,
          rating_history: p.rating ? [p.rating] : [1200],
          match_history: [],
        };
      }
    }

    // Process tournaments and build rating histories
    const tournamentData = [];
    for (const [tid, t] of Object.entries(oldData.tournaments || {})) {
      const rounds = t.roundList || [];
      
      // Convert player IDs to names in the tournament data
      const convertedRounds = rounds.map((round) =>
        (round || []).map((game) => ({
          ...game,
          whiteId: idToName[game.whiteId] || game.whiteId,
          blackId: idToName[game.blackId] || game.blackId,
          // Preserve all rating data
          whiteOrigRating: game.whiteOrigRating,
          whiteNewRating: game.whiteNewRating,
          blackOrigRating: game.blackOrigRating,
          blackNewRating: game.blackNewRating,
          result: game.result,
        }))
      );

      // Track rating changes for each player through the tournament
      const playerRatingUpdates = {};
      
      for (const round of rounds) {
        for (const game of round || []) {
          const whiteName = idToName[game.whiteId];
          const blackName = idToName[game.blackId];
          
          if (whiteName && whiteName !== "________DUMMY________") {
            const whiteKey = getPlayerKey(whiteName);
            if (!playerRatingUpdates[whiteKey]) {
              playerRatingUpdates[whiteKey] = [];
            }
            if (game.whiteNewRating) {
              playerRatingUpdates[whiteKey].push(game.whiteNewRating);
            }
          }
          
          if (blackName && blackName !== "________DUMMY________") {
            const blackKey = getPlayerKey(blackName);
            if (!playerRatingUpdates[blackKey]) {
              playerRatingUpdates[blackKey] = [];
            }
            if (game.blackNewRating) {
              playerRatingUpdates[blackKey].push(game.blackNewRating);
            }
          }
        }
      }

      // Add rating updates to players
      for (const [playerKey, ratings] of Object.entries(playerRatingUpdates)) {
        if (playersMap[playerKey]) {
          playersMap[playerKey].rating_history.push(...ratings);
        }
      }

      tournamentData.push({
        name: t.name,
        date: t.date,
        playerIds: (t.playerIds || []).map((id) => idToName[id] || id),
        roundList: convertedRounds,
      });
    }

    // Upsert players
    for (const playerData of Object.values(playersMap)) {
      const { data: existing } = await supabase
        .from("player_ahs")
        .select("*")
        .eq("name", playerData.name)
        .single();

      if (existing) {
        // Merge rating histories (remove duplicates)
        const allRatings = [
          ...(existing.rating_history || []),
          ...playerData.rating_history,
        ];
        const uniqueRatings = [...new Set(allRatings)];

        const mergedMatches = [
          ...(existing.match_history || []),
          ...playerData.match_history,
        ];

        await supabase
          .from("player_ahs")
          .update({
            rating_history: uniqueRatings,
            match_history: mergedMatches,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
      } else {
        await supabase.from("player_ahs").insert([playerData]);
      }
    }

    // Save tournaments
    for (const eventData of tournamentData) {
      const { data: existing } = await supabase
        .from("events_ahs")
        .select("*")
        .eq("name", eventData.name)
        .eq("event_format", "tournament")
        .single();

      if (!existing) {
        await supabase.from("events_ahs").insert([
          {
            name: eventData.name,
            event_data: eventData,
            event_format: "tournament",
          },
        ]);
      }
    }
  }

  async function saveEvent(eventData) {
    if (!supabase) return;

    await supabase.from("events_ahs").insert([
      {
        name: eventData.name,
        event_data: eventData.event_data || eventData,
        event_format: eventData.event_format || "tournament",
      },
    ]);
  }

  async function handleSideEventMerge() {
    try {
      setSideEventStatus("");
      if (!sideEventTeam.trim()) {
        setSideEventStatus("Please enter a team name.");
        return;
      }

      const parsed = JSON.parse(sideEventText);
      if (!parsed.all_matches || !Array.isArray(parsed.all_matches)) {
        setSideEventStatus("Invalid format: missing all_matches array");
        return;
      }

      const matches = parsed.all_matches.filter(
        (m) => m.team1 === sideEventTeam || m.team2 === sideEventTeam
      );

      if (matches.length === 0) {
        setSideEventStatus(`No matches found for team "${sideEventTeam}".`);
        return;
      }

      // Build opponent rating lookup from parsed data
      const opponentRatings = {};
      for (const match of parsed.all_matches) {
        for (const p of [...(match.players1 || []), ...(match.players2 || [])]) {
          if (p.name && p.rating) {
            opponentRatings[p.name] = p.rating;
          }
        }
      }

      // Process matches and update players with ELO
      const playerUpdates = {};

      for (const match of matches) {
        const isTeam1 = match.team1 === sideEventTeam;
        const teamPlayers = isTeam1 ? match.players1 || [] : match.players2 || [];
        const oppPlayers = isTeam1 ? match.players2 || [] : match.players1 || [];
        const playerMatches = match.playerMatches || [];

        for (const tp of teamPlayers) {
          const playerName = tp.name;

          // Get or initialize player data
          if (!playerUpdates[playerName]) {
            let { data: player } = await supabase
              .from("player_ahs")
              .select("*")
              .eq("name", playerName)
              .single();

            if (!player) {
              const { data: newPlayer } = await supabase
                .from("player_ahs")
                .insert([
                  {
                    name: playerName,
                    rating_history: [tp.rating || 1200],
                    match_history: [],
                  },
                ])
                .select()
                .single();
              player = newPlayer;
            }

            playerUpdates[playerName] = {
              player,
              newMatches: [],
              ratingChanges: [],
            };
          }

const currentData = playerUpdates[playerName];
// Use the most recent rating from accumulated changes, or fall back to player's rating history
const currentRating = currentData.ratingChanges.length > 0
  ? currentData.ratingChanges[currentData.ratingChanges.length - 1]
  : (currentData.player.rating_history?.[currentData.player.rating_history.length - 1] || 1200);

          // Find opponent and their match
          const pmatch = playerMatches.find(
            (pm) => pm.player1 === tp.name || pm.player2 === tp.name
          );

          const opponentName = pmatch
            ? pmatch.player1 === tp.name
              ? pmatch.player2
              : pmatch.player1
            : oppPlayers[teamPlayers.indexOf(tp)]?.name || "Unknown";

          const opponentRating = opponentRatings[opponentName] || 1200;

          // Determine result
          let result = "loss";
          if (pmatch?.result) {
            const resultLower = pmatch.result.toLowerCase();
            if (resultLower.includes("draw")) {
              result = "draw";
            } else if (resultLower === playerName.toLowerCase()) {
              result = "win";
            }
          } else if (typeof tp.wins === "number") {
            result = tp.wins === 1 ? "win" : tp.wins === 0.5 ? "draw" : "loss";
          }

          // Calculate ELO change
          const eloChange = calculateEloChange(currentRating, opponentRating, result);
          const newRating = currentRating + eloChange;

          // Create match entry
          const newMatch = {
            id: `${match.id}_${tp.name}_${match.round}`,
            date: match.time || parsed.time || new Date().toISOString(),
            round: match.round ?? null,
            result,
            color: tp.color?.startsWith("w") ? "white" : "black",
            opponentName,
            opponentRating,
            opponentTeam: isTeam1 ? match.team2 : match.team1,
            event_format: "side",
            ratingBefore: currentRating,
            ratingAfter: newRating,
            ratingChange: eloChange,
          };

          currentData.newMatches.push(newMatch);
          currentData.ratingChanges.push(newRating);
        }
      }

      // Save all player updates to database
      for (const [playerName, data] of Object.entries(playerUpdates)) {
        const updatedRatingHistory = [
          ...(data.player.rating_history || []),
          ...data.ratingChanges,
        ];

        const updatedMatchHistory = [
          ...(data.player.match_history || []),
          ...data.newMatches,
        ];

        await supabase
          .from("player_ahs")
          .update({
            rating_history: updatedRatingHistory,
            match_history: updatedMatchHistory,
            updated_at: new Date().toISOString(),
          })
          .eq("id", data.player.id);
      }

      await loadData();
      setSideEventStatus(
        `Merged ${Object.keys(playerUpdates).length} players' side event matches with ELO updates.`
      );
    } catch (e) {
      setSideEventStatus("Error: " + (e.message || String(e)));
    }
  }

  async function handleAddMatch() {
    try {
      setManualStatus("");
      if (!manualPlayer.trim() || !manualOpponent.trim()) {
        setManualStatus("Please enter both player and opponent names.");
        return;
      }

      if (!supabase) {
        setManualStatus("Supabase not configured");
        return;
      }

      // Check if opponent exists
      const { data: opponentData } = await supabase
        .from("player_ahs")
        .select("*")
        .eq("name", manualOpponent.trim())
        .single();

      if (!opponentData) {
        setManualStatus("Opponent not found in database. Cannot add match.");
        return;
      }

      const opponentRating = opponentData.rating_history?.[opponentData.rating_history.length - 1] || 1200;

      // Get or create player
      let { data: playerData } = await supabase
        .from("player_ahs")
        .select("*")
        .eq("name", manualPlayer.trim())
        .single();

      if (!playerData) {
        // Create new player
        const { data: newPlayer } = await supabase
          .from("player_ahs")
          .insert([
            {
              name: manualPlayer.trim(),
              rating_history: [1200],
              match_history: [],
            },
          ])
          .select()
          .single();
        playerData = newPlayer;
      }

      const currentRating = playerData.rating_history?.[playerData.rating_history.length - 1] || 1200;

      // Calculate ELO change
      const eloChange = calculateEloChange(currentRating, opponentRating, manualResult);
      const newRating = currentRating + eloChange;

      // Create match entry
      const newMatch = {
        id: `manual_${Date.now()}_${manualPlayer.trim()}`,
        date: new Date(manualDate).toISOString(),
        round: null,
        result: manualResult,
        color: manualColor,
        opponentName: manualOpponent.trim(),
        opponentRating,
        opponentTeam: null,
        event_format: "side",
        ratingBefore: currentRating,
        ratingAfter: newRating,
        ratingChange: eloChange,
      };

      // Update player
      const updatedRatingHistory = [...(playerData.rating_history || []), newRating];
      const updatedMatchHistory = [...(playerData.match_history || []), newMatch];

      await supabase
        .from("player_ahs")
        .update({
          rating_history: updatedRatingHistory,
          match_history: updatedMatchHistory,
          updated_at: new Date().toISOString(),
        })
        .eq("id", playerData.id);

      await loadData();
      setManualStatus(`Match added successfully! Rating: ${currentRating} → ${newRating} (${eloChange > 0 ? '+' : ''}${eloChange})`);
      setManualPlayer("");
      setManualOpponent("");
      setManualResult("win");
      setManualColor("white");
    } catch (e) {
      setManualStatus("Error: " + (e.message || String(e)));
    }
  }

  async function handleEditRating() {
    try {
      setRatingEditStatus("");
      if (!ratingEditPlayer.trim() || !ratingEditValue.trim()) {
        setRatingEditStatus("Please enter both player name and rating value.");
        return;
      }

      if (!supabase) {
        setRatingEditStatus("Supabase not configured");
        return;
      }

      const newRating = parseInt(ratingEditValue);
      if (isNaN(newRating)) {
        setRatingEditStatus("Rating must be a valid number.");
        return;
      }

      // Find player
      const { data: playerData } = await supabase
        .from("player_ahs")
        .select("*")
        .eq("name", ratingEditPlayer.trim())
        .single();

      if (!playerData) {
        setRatingEditStatus("Player not found.");
        return;
      }

      // Update rating history by adding new rating
      const updatedRatingHistory = [...(playerData.rating_history || []), newRating];

      await supabase
        .from("player_ahs")
        .update({
          rating_history: updatedRatingHistory,
          updated_at: new Date().toISOString(),
        })
        .eq("id", playerData.id);

      await loadData();
      setRatingEditStatus(`Rating updated successfully to ${newRating}`);
      setRatingEditPlayer("");
      setRatingEditValue("");
    } catch (e) {
      setRatingEditStatus("Error: " + (e.message || String(e)));
    }
  }

  async function handleDeleteLastMatch(playerId) {
    try {
      if (!supabase) return;

      const player = players.find((p) => p.id === playerId);
      if (!player) return;

      const matchHistory = player.match_history || [];
      if (matchHistory.length === 0) {
        alert("No matches to delete");
        return;
      }

      if (!window.confirm(`Delete last match for ${player.name}?`)) {
        return;
      }

      // Remove last match
      const updatedMatchHistory = matchHistory.slice(0, -1);

      // Remove last rating from history
      const ratingHistory = player.rating_history || [];
      const updatedRatingHistory = ratingHistory.slice(0, -1);

      await supabase
        .from("player_ahs")
        .update({
          rating_history: updatedRatingHistory,
          match_history: updatedMatchHistory,
          updated_at: new Date().toISOString(),
        })
        .eq("id", playerId);

      await loadData();
      alert("Last match deleted successfully");
    } catch (e) {
      alert("Error deleting match: " + e.message);
    }
  }

  function toggleRound(eventId, rIdx) {
    setExpandedRounds((prev) => {
      const key = `${eventId}_${rIdx}`;
      return { ...prev, [key]: !prev[key] };
    });
  }

  function openEvent(event) {
    setSelectedEvent(event);
    setSelectedPlayer(null);
    setExpandedRounds({});
  }

  function closeEvent() {
    setSelectedEvent(null);
    setExpandedRounds({});
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <header className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-indigo-700">♟️ AHS Chess Dashboard</h1>
          <div className="flex gap-3 items-center">
            {!isAdminUnlocked && (
              <button
                onClick={handleUnlockAdmin}
                className="px-4 py-2 bg-yellow-500 text-white rounded shadow hover:bg-yellow-600"
              >
                Unlock Admin Controls
              </button>
            )}
            {selectedPlayer && (
              <button
                onClick={() => setSelectedPlayer(null)}
                className="text-sm text-gray-500 hover:text-gray-800"
              >
                ← Back to all players
              </button>
            )}
            {selectedEvent && (
              <button
                onClick={closeEvent}
                className="text-sm text-gray-500 hover:text-gray-800"
              >
                ← Back to events
              </button>
            )}
          </div>
        </header>

        {/* Admin Controls */}
        {!selectedPlayer && !selectedEvent && isAdminUnlocked && (
          <>
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-2xl shadow p-4">
                <h2 className="font-semibold mb-2">Paste JSON</h2>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  rows={8}
                  className="w-full border rounded p-2 font-mono text-xs"
                  placeholder="Paste tournament/event JSON here..."
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handlePasteMerge}
                    className="px-4 py-2 bg-indigo-600 text-white rounded shadow"
                  >
                    Merge & Save
                  </button>
                  <button
                    onClick={() => setPasteText("")}
                    className="px-4 py-2 bg-gray-100 rounded"
                  >
                    Clear
                  </button>
                </div>
                <p className="text-sm mt-2 text-gray-600">
                  Status: {status || "Idle"}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow p-4">
                <h2 className="font-semibold mb-2">Side Event Matches</h2>
                <textarea
                  value={sideEventText}
                  onChange={(e) => setSideEventText(e.target.value)}
                  rows={8}
                  className="w-full border rounded p-2 font-mono text-xs"
                  placeholder="Paste side event matches JSON here..."
                />
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="text"
                    value={sideEventTeam}
                    onChange={(e) => setSideEventTeam(e.target.value)}
                    placeholder="Enter team name"
                    className="border rounded p-2 text-sm flex-grow"
                  />
                  <button
                    onClick={handleSideEventMerge}
                    className="px-4 py-2 bg-green-600 text-white rounded shadow"
                  >
                    Merge (Rated)
                  </button>
                </div>
                <p className="text-sm mt-2 text-gray-600">
                  {sideEventStatus || "Idle - Side events are rated with K=32"}
                </p>
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl shadow p-4">
                <h2 className="font-semibold mb-2">Add Match</h2>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={manualPlayer}
                    onChange={(e) => setManualPlayer(e.target.value)}
                    placeholder="Player name"
                    className="w-full border rounded p-2 text-sm"
                  />
                  <input
                    type="text"
                    value={manualOpponent}
                    onChange={(e) => setManualOpponent(e.target.value)}
                    placeholder="Opponent name (must exist)"
                    className="w-full border rounded p-2 text-sm"
                  />
                  <select
                    value={manualResult}
                    onChange={(e) => setManualResult(e.target.value)}
                    className="w-full border rounded p-2 text-sm"
                  >
                    <option value="win">Win</option>
                    <option value="draw">Draw</option>
                    <option value="loss">Loss</option>
                  </select>
                  <select
                    value={manualColor}
                    onChange={(e) => setManualColor(e.target.value)}
                    className="w-full border rounded p-2 text-sm"
                  >
                    <option value="white">White</option>
                    <option value="black">Black</option>
                  </select>
                  <input
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    className="w-full border rounded p-2 text-sm"
                  />
                  <button
                    onClick={handleAddMatch}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded shadow"
                  >
                    Add Match
                  </button>
                </div>
                <p className="text-xs mt-2 text-gray-600">{manualStatus || "Idle"}</p>
              </div>

              <div className="bg-white rounded-2xl shadow p-4">
                <h2 className="font-semibold mb-2">Edit Player Rating</h2>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={ratingEditPlayer}
                    onChange={(e) => setRatingEditPlayer(e.target.value)}
                    placeholder="Player name"
                    className="w-full border rounded p-2 text-sm"
                  />
                  <input
                    type="number"
                    value={ratingEditValue}
                    onChange={(e) => setRatingEditValue(e.target.value)}
                    placeholder="New rating"
                    className="w-full border rounded p-2 text-sm"
                  />
                  <button
                    onClick={handleEditRating}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded shadow"
                  >
                    Update Rating
                  </button>
                </div>
                <p className="text-xs mt-2 text-gray-600">{ratingEditStatus || "Adds new rating to history"}</p>
              </div>

              <div className="bg-white rounded-2xl shadow p-4">
                <h2 className="font-semibold mb-2">Delete Last Match</h2>
                <p className="text-xs text-gray-600 mb-2">Select a player from the list below</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {playerList.map((p) => (
                    <div key={p.id} className="flex items-center justify-between border-b pb-1">
                      <span className="text-sm">{p.name}</span>
                      <button
                        onClick={() => handleDeleteLastMatch(p.id)}
                        className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {/* Overview */}
        {!selectedPlayer && !selectedEvent && (
          <>
            <section className="bg-white rounded-2xl shadow p-4 mb-6">
              <h3 className="font-semibold">Quick Stats</h3>
              <div className="mt-2 text-sm text-gray-700">
                <div>Players: {playerList.length}</div>
                <div>Events: {events.length}</div>
                <div>Tournaments: {tournamentList.length}</div>
                <div>Total games: {allGames.length}</div>
              </div>
            </section>

            {/* Players */}
            <section className="bg-white rounded-2xl shadow p-4 mb-6">
              <h2 className="text-xl font-semibold mb-3">Players</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-gray-600 border-b">
                      <th className="p-2">Name</th>
                      <th className="p-2">Rating</th>
                      <th className="p-2">Official</th>
                      <th className="p-2">Side Events</th>
                      <th className="p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {playerList.map((p) => (
                      <tr
                        key={p.id}
                        className="border-t hover:bg-indigo-50 cursor-pointer"
                        onClick={() => setSelectedPlayer(p.id)}
                      >
                        <td className="p-2">{p.name}</td>
                        <td className="p-2">{p.currentRating}</td>
                        <td className="p-2">{p.officialGames}</td>
                        <td className="p-2">{p.sideEventGames}</td>
                        <td className="p-2">{p.totalGames}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Tournaments */}
            <section className="bg-white rounded-2xl shadow p-4 mb-6">
              <h2 className="text-xl font-semibold mb-3">Tournaments</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-gray-600 border-b">
                      <th className="p-2">Name</th>
                      <th className="p-2">Date</th>
                      <th className="p-2">Players</th>
                      <th className="p-2">Rounds</th>
                      <th className="p-2">Games</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tournamentList.map((t) => (
                      <tr
                        key={t.id}
                        className="border-t hover:bg-indigo-50 cursor-pointer"
                        onClick={() => openEvent(t)}
                      >
                        <td className="p-2">{t.name}</td>
                        <td className="p-2">
                          {t.eventData.date
                            ? dayjs(t.eventData.date).format("YYYY-MM-DD")
                            : dayjs(t.created_at).format("YYYY-MM-DD")}
                        </td>
                        <td className="p-2">{t.playerCount}</td>
                        <td className="p-2">{t.roundCount}</td>
                        <td className="p-2">{t.gameCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {/* Event Detail */}
        {selectedEvent && (
          <section className="bg-white rounded-2xl shadow p-4 mb-6">
            <h2 className="text-2xl font-semibold mb-4">{selectedEvent.name}</h2>
            <div className="text-sm text-gray-600 mb-6">
              <div>
                Date:{" "}
                {selectedEvent.eventData.date
                  ? dayjs(selectedEvent.eventData.date).format("YYYY-MM-DD")
                  : dayjs(selectedEvent.created_at).format("YYYY-MM-DD")}
              </div>
              <div>Players: {selectedEvent.playerCount}</div>
              <div>Rounds: {selectedEvent.roundCount}</div>
              <div>Games: {selectedEvent.gameCount}</div>
            </div>

            <div className="space-y-4">
              {(selectedEvent.eventData.roundList || []).map((round, rIdx) => {
                const key = `${selectedEvent.id}_${rIdx}`;
                const expanded = !!expandedRounds[key];
                const games = round || [];

                return (
                  <div key={key} className="border rounded-lg overflow-hidden">
                    <div
                      className="p-3 bg-gray-100 flex items-center justify-between cursor-pointer"
                      onClick={() => toggleRound(selectedEvent.id, rIdx)}
                    >
                      <div>
                        <div className="font-medium">Round {rIdx + 1}</div>
                        <div className="text-sm text-gray-600">
                          {games.length} games
                        </div>
                      </div>
                      <div className="text-sm text-indigo-600">
                        {expanded ? "Collapse" : "Expand"}
                      </div>
                    </div>

                    {expanded && (
                      <div className="p-3 bg-white">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="text-gray-600 border-b">
                              <th className="p-2">White</th>
                              <th className="p-2">Black</th>
                              <th className="p-2">Result</th>
                              <th className="p-2">White Rating</th>
                              <th className="p-2">Black Rating</th>
                            </tr>
                          </thead>
                          <tbody>
                            {games.map((g, gIdx) => (
                              <tr key={gIdx} className="border-t">
                                <td className="p-2">{g.whiteId || "—"}</td>
                                <td className="p-2">{g.blackId || "—"}</td>
                                <td className="p-2">{g.result || "—"}</td>
                                <td className="p-2">
                                  {g.whiteNewRating ?? g.whiteOrigRating ?? "—"}
                                </td>
                                <td className="p-2">
                                  {g.blackNewRating ?? g.blackOrigRating ?? "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Player Detail */}
        {selectedPlayer && selectedPlayerData && (
          <section className="bg-white rounded-2xl shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-indigo-700">
                {selectedPlayerData.player.name}
              </h2>
              <div className="text-sm text-gray-600">
                Rating:{" "}
                <span className="font-medium">
                  {selectedPlayerData.player.rating_history?.[
                    selectedPlayerData.player.rating_history.length - 1
                  ] || 1200}
                </span>
              </div>
            </div>

            {selectedPlayerData.ratingHistory.length > 0 && (
              <div className="h-64 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedPlayerData.ratingHistory}>
                    <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                    <XAxis dataKey="index" />
                    <YAxis domain={["auto", "auto"]} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="rating"
                      stroke="#6366f1"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <h3 className="font-semibold text-lg mb-2">Official Games</h3>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="p-2">Event</th>
                    <th className="p-2">Round</th>
                    <th className="p-2">Opponent</th>
                    <th className="p-2">Color</th>
                    <th className="p-2">Result</th>
                    <th className="p-2">Date</th>
                    <th className="p-2">Rating Change</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPlayerData.officialGames.map((g, idx) => {
                    const isWhite = g.whiteId === selectedPlayerData.playerName;
                    const oppName = isWhite ? g.blackId : g.whiteId;
                    const result = g.result?.toLowerCase().includes("draw")
                      ? "Draw"
                      : (isWhite && g.result?.toLowerCase().includes("white")) ||
                        (!isWhite && g.result?.toLowerCase().includes("black"))
                      ? "Win"
                      : g.result?.toLowerCase().includes("abort")
                      ? "Aborted"
                      : "Loss";
                      console.log(g)
                    const ratingChange = (isWhite? g.whiteNewRating - g.whiteOrigRating : g.blackNewRating - g.blackOrigRating) || 0;
                    return (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{g.eventName}</td>
                        <td className="p-2">{g.round}</td>
                        <td className="p-2">{oppName === "________DUMMY________" ? "BYE" : oppName}</td>
                        <td className="p-2">{isWhite ? "White" : "Black"}</td>
                        <td className="p-2">{result}</td>
                        <td className="p-2">
                          {dayjs(g.date).format("YYYY-MM-DD")}
                        </td>
                        <td className={`p-2 font-medium ${
                          ratingChange > 0 ? "text-green-600" : ratingChange < 0 ? "text-red-600" : "text-gray-600"
                        }`}>
                          {ratingChange > 0 ? "+" : ""}{ratingChange || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <h3 className="font-semibold text-lg mb-2">Side Event Matches (Rated)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="p-2">Date</th>
                    <th className="p-2">Round</th>
                    <th className="p-2">Opponent</th>
                    <th className="p-2">Opp Rating</th>
                    <th className="p-2">Color</th>
                    <th className="p-2">Result</th>
                    <th className="p-2">Rating Change</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPlayerData.sideEventMatches.map((m, idx) => {
                    // Alternate colors if not provided: first game white, then black, etc.
                    const displayColor = m.color || (idx % 2 === 0 ? "white" : "black");
                    
                    return (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          {dayjs(m.date).format("YYYY-MM-DD")}
                        </td>
                        <td className="p-2">{m.round !== null && m.round !== undefined ? m.round + 1 : "—"}</td>
                        <td className="p-2">
                          {m.opponentName || m.opponentTeam}
                        </td>
                        <td className="p-2">{m.opponentRating || "—"}</td>
                        <td className="p-2 capitalize">{displayColor}</td>
                        <td className="p-2 capitalize">
                          {m.result === "win" ? "Win" : m.result === "draw" ? "Draw" : "Loss"}
                        </td>
                        <td className={`p-2 font-medium ${
                          m.ratingChange > 0 ? "text-green-600" : m.ratingChange < 0 ? "text-red-600" : "text-gray-600"
                        }`}>
                          {m.ratingChange > 0 ? "+" : ""}{m.ratingChange || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>
    </div>
  );
}